/**
 * Soft-navigation state machine for the embedded Atlas iframe
 * (atlas-iframe-warm-navigation, Story 03).
 *
 * Warbler used to change Atlas pages by blanking `iframeSrc` and rebuilding
 * it, which reloaded the iframe and rebooted the whole Atlas app — discarding
 * the CopilotKit conversation. It now posts a NAVIGATE message and lets Atlas
 * route itself.
 *
 * Extracted from the component (like buildAtlasContext in atlasEmbedContext.ts)
 * because the two hazards here are pure logic and deserve direct tests:
 *
 *  - **Echo loop.** Atlas reports its own navigation, Warbler pushes its
 *    router to match, the `agent`/`tab` props change, and the watchers would
 *    dispatch that same navigation straight back to Atlas. `lastSyncedPath`
 *    tracks the destination both sides already agree on, so a navigation that
 *    merely reflects one already applied is never re-sent. It replaces the
 *    old `tabChangeFromAtlas` boolean, which only guarded a single hop and
 *    could not cover agent changes.
 *
 *  - **Version skew.** Warbler and Atlas deploy independently. A newer Warbler
 *    talking to an Atlas build with no NAVIGATE subscriber would leave the
 *    iframe frozen on the old page — worse than a slow reload. Every dispatch
 *    arms a deadline; without an acknowledgement it falls back to the original
 *    hard reload. That also doubles as a kill switch.
 *
 *  - **Premature completion.** Atlas acknowledges NAVIGATE before its router
 *    has fetched the route. Treating the ACK as "arrived" left the old page
 *    visible under the new URL with no feedback (2026-09-16 trace, spec
 *    2026-09-17-atlas-soft-nav-arrival-design.md). The ACK now only swaps the
 *    ACK deadline for an arrival deadline; the NAVIGATION echo every Atlas
 *    destination sends on commit is the real completion signal.
 *
 * Timers are injected so tests are deterministic rather than clock-dependent.
 */

/** Default grace period for Atlas to acknowledge before falling back. */
export const DEFAULT_ACK_TIMEOUT_MS = 300;

/**
 * Default wait, after the ACK, for Atlas to echo NAVIGATION for the committed
 * route before giving up and hard-reloading (spec D2). The ACK only means
 * Atlas accepted the path — Next.js still has to fetch the route, which on a
 * slow link takes seconds and can be discarded by a later navigation.
 */
export const DEFAULT_ARRIVAL_TIMEOUT_MS = 10000;

/**
 * Default delay before the pending indicator is shown (spec D3). Warm
 * navigations complete in ~40 ms; showing the overlay immediately would flash.
 */
export const DEFAULT_PENDING_INDICATOR_DELAY_MS = 200;

/**
 * How a navigation resolved. Emitted as one event with an `outcome` field
 * rather than separate counters, so a single query answers "how are
 * Atlas-to-Atlas transitions resolving?".
 *
 *  - `soft`       — Atlas acknowledged AND echoed the committed route; no
 *                   reload. The happy path. Latency is dispatch → echo.
 *  - `fallback`   — no acknowledgement in time, iframe rebuilt. Non-zero in
 *                   production means version skew between the Warbler and
 *                   Atlas deploys, or a path Warbler believes Atlas serves and
 *                   it does not.
 *  - `stalled`    — acknowledged, but no NAVIGATION echo within the arrival
 *                   deadline; iframe rebuilt. Non-zero means slow/failed route
 *                   fetches in Atlas, or a destination that never echoes.
 *  - `suppressed` — an echo of a navigation Atlas performed itself; nothing
 *                   was dispatched. Should track the volume of `soft`.
 */
export type AtlasNavOutcome = 'soft' | 'fallback' | 'stalled' | 'suppressed';

export interface AtlasNavTelemetry {
  outcome: AtlasNavOutcome;
  path: string;
  /** Dispatch-to-resolution time; null for suppressed echoes (nothing sent). */
  latencyMs: number | null;
}

export interface AtlasSoftNavDeps {
  /** Post a NAVIGATE message to the iframe. */
  postNavigate: (path: string, query: string) => void;
  /** Fall back to rebuilding the iframe src (today's behaviour). */
  hardReload: () => void;
  setTimer: (fn: () => void, ms: number) => number;
  clearTimer: (id: number) => void;
  /** Injected so latency is measurable without a real clock in tests. */
  now?: () => number;
  onTelemetry?: (event: AtlasNavTelemetry) => void;
  ackTimeoutMs?: number;
  /**
   * Show / hide the in-progress indicator for an acknowledged navigation whose
   * route has not committed yet. Called with `true` once the grace period
   * passes and with `false` when the navigation resolves, is superseded, or the
   * controller is disposed — never with `false` unless `true` was sent first.
   */
  setPending?: (pending: boolean) => void;
  arrivalTimeoutMs?: number;
  pendingIndicatorDelayMs?: number;
}

/** Strip the query and hash, ensure a leading slash, drop a trailing slash. */
export function normalizeWarblerPath(route: string): string {
  const bare = (route || '').split('?')[0].split('#')[0].trim();
  if (!bare) return '';
  const withSlash = bare.startsWith('/') ? bare : `/${bare}`;
  return withSlash.length > 1 && withSlash.endsWith('/')
    ? withSlash.slice(0, -1)
    : withSlash;
}

/** The Atlas-relative path for an agent/tab pair, e.g. `/setup/variants`. */
export function toAtlasPath(agent: string, tab: string): string {
  const segments = [agent, tab].filter((s) => !!s && String(s).length > 0);
  return segments.length ? `/${segments.join('/')}` : '/';
}

/** The Warbler path for the same pair, e.g. `/assistant/setup/variants`. */
export function toWarblerPath(agent: string, tab: string): string {
  const atlasPath = toAtlasPath(agent, tab);
  return atlasPath === '/' ? '/assistant' : `/assistant${atlasPath}`;
}

export class AtlasSoftNavController {
  private readonly deps: AtlasSoftNavDeps;
  private readonly ackTimeoutMs: number;
  private readonly arrivalTimeoutMs: number;
  private readonly pendingIndicatorDelayMs: number;

  /** The destination Warbler and Atlas currently agree on. */
  private lastSyncedPath: string | null = null;
  /** The Atlas path awaiting acknowledgement, if any. */
  private pendingAckPath: string | null = null;
  /**
   * The Atlas path acknowledged but not yet echoed back as a committed route.
   * Kept separately from pendingAckPath so telemetry can name the dispatched
   * path even when Atlas echoes a different one (spec D5).
   */
  private pendingArrivalPath: string | null = null;
  private ackTimerId: number | null = null;
  private graceTimerId: number | null = null;
  private deadlineTimerId: number | null = null;
  /** Whether setPending(true) has been sent for the current navigation. */
  private indicatorShown = false;
  /** When the pending dispatch was posted, for latency measurement. */
  private dispatchedAt: number | null = null;

  constructor(deps: AtlasSoftNavDeps) {
    this.deps = deps;
    this.ackTimeoutMs = deps.ackTimeoutMs ?? DEFAULT_ACK_TIMEOUT_MS;
    this.arrivalTimeoutMs = deps.arrivalTimeoutMs ?? DEFAULT_ARRIVAL_TIMEOUT_MS;
    this.pendingIndicatorDelayMs =
      deps.pendingIndicatorDelayMs ?? DEFAULT_PENDING_INDICATOR_DELAY_MS;
  }

  /**
   * Record the destination the iframe was initially built for. Called once the
   * iframe src is constructed, so the first watcher fire for an unchanged
   * route does not dispatch a pointless navigation.
   */
  seed(warblerPath: string): void {
    this.lastSyncedPath = normalizeWarblerPath(warblerPath);
  }

  /**
   * Handle an `agent`/`tab` change. Returns whether a NAVIGATE was dispatched;
   * false means the target was already in sync (an echo of a navigation Atlas
   * performed itself).
   *
   * A dispatch while an earlier navigation is still awaiting its echo
   * supersedes it silently (spec D6): the user has moved on, so the old
   * deadline must never reload the iframe out from under the new page.
   */
  requestNavigation(agent: string, tab: string, query: string): boolean {
    const warblerPath = toWarblerPath(agent, tab);
    if (warblerPath === this.lastSyncedPath) {
      this.report('suppressed', toAtlasPath(agent, tab), null);
      return false;
    }

    this.clearPendingNavigation();
    this.lastSyncedPath = warblerPath;
    const atlasPath = toAtlasPath(agent, tab);
    this.pendingAckPath = atlasPath;
    this.dispatchedAt = this.currentTime();
    this.armAckFallback();
    this.deps.postNavigate(atlasPath, query);
    return true;
  }

  /**
   * Handle Atlas's acknowledgement. Only the currently pending path counts —
   * a late ACK from a superseded navigation must not disarm the live one.
   *
   * The ACK is acceptance, not arrival: Atlas sends it before its router has
   * fetched the route. So this only swaps the ACK deadline for the arrival
   * deadline (plus the indicator grace timer); `soft` is reported when the
   * NAVIGATION echo lands in noteAtlasNavigation().
   */
  handleAck(path: string): void {
    if (!this.pendingAckPath) return;
    if (normalizeWarblerPath(path) !== this.pendingAckPath) return;
    this.pendingArrivalPath = this.pendingAckPath;
    this.pendingAckPath = null;
    this.disarmAckFallback();
    this.armArrivalTimers();
  }

  /**
   * Record a navigation Atlas performed on its own, so the router update it
   * triggers in Warbler does not bounce back as a fresh dispatch.
   *
   * This is also the arrival signal: every Atlas destination echoes NAVIGATION
   * once its route commits. Any echo while a dispatch is pending resolves it,
   * even for a different path (spec D5) — Warbler's router follows the echo, so
   * URL and content agree either way and the user is not stuck. An echo that
   * beats the ACK counts as arrival too (the ACK is then implied).
   */
  noteAtlasNavigation(route: string): void {
    this.lastSyncedPath = normalizeWarblerPath(route);
    const dispatched = this.pendingArrivalPath ?? this.pendingAckPath;
    if (dispatched === null) return;
    this.clearPendingNavigation();
    this.report('soft', dispatched, this.elapsedSinceDispatch());
  }

  /** Whether a dispatch is still waiting to be acknowledged. */
  get isAwaitingAck(): boolean {
    return this.pendingAckPath !== null;
  }

  /** Whether an acknowledged dispatch is still waiting for its NAVIGATION echo. */
  get isAwaitingArrival(): boolean {
    return this.pendingArrivalPath !== null;
  }

  dispose(): void {
    this.clearPendingNavigation();
  }

  /** Drop whatever is in flight without reporting: timers, flags, indicator. */
  private clearPendingNavigation(): void {
    this.disarmAckFallback();
    this.disarmArrivalTimers();
    this.hideIndicator();
    this.pendingAckPath = null;
    this.pendingArrivalPath = null;
  }

  private armAckFallback(): void {
    this.disarmAckFallback();
    this.ackTimerId = this.deps.setTimer(() => {
      this.ackTimerId = null;
      const abandoned = this.pendingAckPath;
      this.pendingAckPath = null;
      if (abandoned) {
        this.report('fallback', abandoned, this.elapsedSinceDispatch());
      }
      this.deps.hardReload();
    }, this.ackTimeoutMs);
  }

  private disarmAckFallback(): void {
    if (this.ackTimerId !== null) {
      this.deps.clearTimer(this.ackTimerId);
      this.ackTimerId = null;
    }
  }

  private armArrivalTimers(): void {
    this.disarmArrivalTimers();
    this.graceTimerId = this.deps.setTimer(() => {
      this.graceTimerId = null;
      this.showIndicator();
    }, this.pendingIndicatorDelayMs);
    this.deadlineTimerId = this.deps.setTimer(() => {
      this.deadlineTimerId = null;
      const stalled = this.pendingArrivalPath;
      this.clearPendingNavigation();
      if (stalled) {
        this.report('stalled', stalled, this.elapsedSinceDispatch());
      }
      this.deps.hardReload();
    }, this.arrivalTimeoutMs);
  }

  private disarmArrivalTimers(): void {
    if (this.graceTimerId !== null) {
      this.deps.clearTimer(this.graceTimerId);
      this.graceTimerId = null;
    }
    if (this.deadlineTimerId !== null) {
      this.deps.clearTimer(this.deadlineTimerId);
      this.deadlineTimerId = null;
    }
  }

  private showIndicator(): void {
    if (this.indicatorShown) return;
    this.indicatorShown = true;
    this.deps.setPending?.(true);
  }

  private hideIndicator(): void {
    if (!this.indicatorShown) return;
    this.indicatorShown = false;
    this.deps.setPending?.(false);
  }

  private currentTime(): number {
    return this.deps.now ? this.deps.now() : Date.now();
  }

  private elapsedSinceDispatch(): number | null {
    return this.dispatchedAt === null ? null : this.currentTime() - this.dispatchedAt;
  }

  private report(outcome: AtlasNavOutcome, path: string, latencyMs: number | null): void {
    this.deps.onTelemetry?.({ outcome, path, latencyMs });
  }
}
